import { ReactNode, useMemo } from 'react';
import { BoxProps } from '@mui/material';

import {
  CardContainer,
  Label,
  SubtitleBlock,
  SubtitleIcon,
  SubtitleInline,
  SubtitleSide,
  SubtitleText,
  Title,
  TitleRow,
  Value,
  ValueRow,
} from './styles';
import {
  InputAdormentPositionEnum,
  LabelPositionEnum,
  SectionUnitsEnum,
  Subtitle,
  UnitPositionEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { getSubtitleIconNode } from './utils';
import { getSectionUnitSymbol, getDisplayValue } from '../../utils';

type KpiCardProps = {
  title: string;
  value: string | number;
  icon: ReactNode;
  subtitle: Subtitle | undefined;
  unit: SectionUnitsEnum | undefined;
  unitPosition?: UnitPositionEnum | undefined;
  sectionUnit?: SectionUnitsEnum | null;
  unitSystem?: UnitSystemEnum;
  roundToDecimal?: number | null;
} & BoxProps;

const renderSubtitleContent = (subtitle: Subtitle, unitSystem: UnitSystemEnum) => {
  if (!subtitle?.text) return null;

  const iconNode = getSubtitleIconNode(subtitle.icon);
  const iconPos = subtitle.iconPosition ?? InputAdormentPositionEnum.Start;
  const unitSuffix = subtitle.unit
    ? getSectionUnitSymbol(subtitle.unit as SectionUnitsEnum, unitSystem)
    : '';
  const unitPos = subtitle.unitPosition ?? UnitPositionEnum.End;

  const text = subtitle.text;
  const rawValue = subtitle.value;
  const subtitleRoundToDecimal = subtitle.roundToDecimal ?? null;

  const displayValue =
    rawValue !== null && rawValue !== undefined
      ? getDisplayValue(
          rawValue,
          subtitle.unit as SectionUnitsEnum,
          unitSystem,
          subtitleRoundToDecimal
        )
      : 0;

  const unit = unitSuffix;

  let textWithUnit: string;
  if (!unit) {
    textWithUnit = `${text} ${displayValue}`;
  } else if (unitPos === UnitPositionEnum.Start) {
    textWithUnit = `${text} ${unit} ${displayValue}`;
  } else {
    textWithUnit = `${text} ${displayValue} ${unit}`;
  }

  return (
    <SubtitleInline>
      {iconNode && iconPos === InputAdormentPositionEnum.Start && (
        <SubtitleIcon>{iconNode}</SubtitleIcon>
      )}
      <SubtitleText>{textWithUnit}</SubtitleText>
      {iconNode && iconPos === InputAdormentPositionEnum.End && (
        <SubtitleIcon>{iconNode}</SubtitleIcon>
      )}
    </SubtitleInline>
  );
};

export const KpiCard = ({
  title,
  value,
  icon,
  subtitle,
  unit,
  unitPosition,
  sectionUnit,
  unitSystem = UnitSystemEnum.Metric,
  roundToDecimal,
  ...boxProps
}: KpiCardProps) => {
  const subtitlePos = subtitle?.position ?? LabelPositionEnum.Bottom;

  const titleWithUnit = title;

  const subtitleNode = useMemo(() => {
    return subtitle ? renderSubtitleContent(subtitle, unitSystem) : undefined;
  }, [subtitle, unitSystem]);

  const displayValue = getDisplayValue(value, sectionUnit, unitSystem, roundToDecimal);

  const valueWithUnit = useMemo(() => {
    if (!unit) return displayValue;

    const unitSuffix = getSectionUnitSymbol(unit, unitSystem);
    const position = unitPosition ?? UnitPositionEnum.End;

    if (position === UnitPositionEnum.Start) {
      return `${unitSuffix} ${displayValue}`;
    } else {
      return `${displayValue} ${unitSuffix}`;
    }
  }, [displayValue, unit, unitPosition, unitSystem]);

  return (
    <CardContainer {...boxProps}>
      <TitleRow>
        {icon}
        <Title>{titleWithUnit}</Title>
      </TitleRow>

      {subtitleNode && subtitlePos === LabelPositionEnum.Top && (
        <SubtitleBlock>{subtitleNode}</SubtitleBlock>
      )}

      <ValueRow>
        {subtitleNode && subtitlePos === LabelPositionEnum.Start && (
          <SubtitleSide>{subtitleNode}</SubtitleSide>
        )}

        <Value>{valueWithUnit}</Value>

        {subtitleNode && subtitlePos === LabelPositionEnum.End && (
          <SubtitleSide>{subtitleNode}</SubtitleSide>
        )}
      </ValueRow>

      {subtitleNode && subtitlePos === LabelPositionEnum.Bottom && <Label>{subtitleNode}</Label>}
    </CardContainer>
  );
};
